import { useRef, useEffect, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import PropTypes from "prop-types"
import { capitalize, get } from "lodash"
import { DESC } from "helpers/sorts"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"
import { useSourceTemplateMetricIds } from "../../hooks/use-source-displayed-metrics"
import { useCompositionVisibility } from "../../hooks/use-source-displayed-metric-ids"
import { MODEL_METADATA_SHAPE, INSTRUMENTS_WITH_METADATA_SHAPE, INSTRUMENT_COLUMNS } from "connected-components/asset-view/prop-types"

export default function AssetViewMarginalContribution({ modelMetadata, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications, flattenedMetrics, onMetricsChange }) {
  const contextData = useAssetViewData()
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isEndVsStartDateComparison,
  } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const attributionMethod = contextBase ? ATTRIBUTION_METHODS.MCTE : ATTRIBUTION_METHODS.MCAR

  const dispatch = useDispatch()
  const displayedMessageRef = useRef(null)

  useEffect(() => {
    if (!groupBy && displayedMessageRef.current) {
      displayedMessageRef.current = null
    }
    return () => {
      displayedMessageRef.current = null
    }
  }, [groupBy])

  const getResponseMessage = useCallback((result) => {
    const message = get(result, "apiMessages.messages.0.message", null)
    // Sometimes the same message will be returned multiple times, so we need to avoid displaying it multiple times
    if (message && !displayedMessageRef.current) {
      displayedMessageRef.current = message
      dispatch(NotificationCreators.add("warning", null, message))
    }
  }, [dispatch])

  const gatherers = useTemplateGatherers(contextData, "marginal", {
    attributionMethod,
    classifications,
    getResponseMessage,
  })

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "marginalContribution", isActive: !!contextBase }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const metricOptions = useMemo(() => ({ prefix: "marginal", includeAllSubCategories: true }), [])

  const compositionVisibility = useCompositionVisibility(instrumentColumns, original)

  useSourceTemplateMetricIds({
    flattenedMetrics,
    onMetricsChange,
    metricOptions,
    compositionVisibility,
  })

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]]?.suffix ?? ""

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.marginalContribution",
      downloadName: "Assets - Marginal Contribution",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      // Analytics callbacks
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
    }}
  />
}

AssetViewMarginalContribution.propTypes = {
  classifications: CLASSIFICATIONS_SHAPE,
  disableActiveTrading: PropTypes.bool,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  instrumentColumns: INSTRUMENT_COLUMNS.isRequired,
  flattenedMetrics: PropTypes.object,
  onMetricsChange: PropTypes.func,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
}
